import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCard?: string;
  structuredData?: object | object[];
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  ogImage,
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterCard = 'summary_large_image',
  structuredData,
}: SEOProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);
    if (ogType) updateMetaTag('og:type', ogType, true);
    if (ogUrl) updateMetaTag('og:url', ogUrl, true);
    if (ogImage) updateMetaTag('og:image', ogImage, true);
    if (twitterCard) updateMetaTag('twitter:card', twitterCard);
    if (twitterTitle) updateMetaTag('twitter:title', twitterTitle);
    if (twitterDescription) updateMetaTag('twitter:description', twitterDescription);
    if (twitterImage) updateMetaTag('twitter:image', twitterImage);

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', today);

    // Add structured data
    if (structuredData) {
      const scriptId = 'structured-data';
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      // Handle both single object and array of objects
      script.textContent = JSON.stringify(structuredData);
    }

    return () => {
      // Cleanup structured data on unmount
      if (structuredData) {
        const script = document.getElementById('structured-data');
        if (script) {
          script.remove();
        }
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogUrl, ogImage, twitterTitle, twitterDescription, twitterImage, twitterCard, structuredData]);
};

export const getBaseUrl = () => {
  return import.meta.env.VITE_SITE_URL || 'https://asherreviews.com';
};
